// ExamResult.js
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaCheck, FaTimes, FaStar, FaRedo, FaHome, FaBook, FaClock, FaAward, FaInfoCircle, FaPrint, FaChartBar, FaExclamationTriangle } from 'react-icons/fa';
import { GiSpeedometer, GiPencil } from 'react-icons/gi';
import { sendApiRequest } from '../utils/api';
import { useParams } from 'react-router-dom';
import Logo from "../assets/logo2.jpeg";
import html2canvas from 'html2canvas';

// Import jsPDF and autoTable correctly
import jsPDF from 'jspdf';

// Import autoTable function directly
import autoTable from 'jspdf-autotable';
import LogoLoader from './LogoLoader';
import ComparePassageModal from './ComparePassageModal';

const ExamResult = () => {
  const { state } = useLocation();
  const { stats } = state || {};
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeStat, setActiveStat] = useState(null);
  const [showMistakeDetails, setShowMistakeDetails] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);


  const [activeErrorType, setActiveErrorType] = useState(null);
  const errorRefs = useRef({});



  const contentRef = useRef();

  const ERROR_BADGES = {
    extra: {
      label: 'Additions',
      bg: 'bg-red-100',
      dot: 'bg-red-500',
      text: 'text-red-700'
    },
    missing: {
      label: 'Omissions',
      bg: 'bg-gray-100',
      dot: 'bg-gray-500',
      text: 'text-gray-700'
    },
    spelling: {
      label: 'Spelling Mistakes',
      bg: 'bg-yellow-100',
      dot: 'bg-yellow-500',
      text: 'text-yellow-700',
      mapTo: 'incorrect' // 🔥 IMPORTANT
    },
    capitalization: {
      label: 'Capitalization Mistakes',
      bg: 'bg-blue-100',
      dot: 'bg-blue-500',
      text: 'text-blue-700'
    },
    punctuation: {
      label: 'Punctuation Mistakes',
      bg: 'bg-purple-100',
      dot: 'bg-purple-500',
      text: 'text-purple-700'
    },
    substitution: {
      label: 'Substitution Mistakes',
      bg: 'bg-red-200',
      dot: 'bg-red-600',
      text: 'text-red-800'
    }
  };

  const errorBreakdown = Object.entries(stats?.detailedPenalties || {})
    .filter(([_, data]) => data.count > 0);

  console.log('Error Breakdown:', errorBreakdown);


  useEffect(() => {
    if (!activeErrorType) return;

    const els = errorRefs.current[activeErrorType];

    if (els && els.length > 0) {
      els[0].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [activeErrorType]);



  // Handle report submission
  const handleReportSubmit = async (e) => {
    e.preventDefault();
    if (!reportReason.trim()) return;

    setIsSubmittingReport(true);

    try {

      const response = await sendApiRequest('api/reports', { reason: reportReason, transcriptionId: id }, 'POST');


      setReportSubmitted(true);
      setTimeout(() => {
        setShowReportModal(false);
        setReportSubmitted(false);
        setReportReason('');
      }, 2000);
    } catch (error) {
      console.error('Failed to submit report:', error);
      alert('Failed to submitted report. Please try again.');
    } finally {
      setIsSubmittingReport(false);
    }
  };

  const reportReasons = [
    'Incorrect transcription',
    'Inappropriate content',
    'Audio quality issues',
    'Other (please specify)'
  ];

  if (!stats) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center max-w-md">
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaTimes className="text-yellow-600 text-3xl" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">No Results Found</h2>
          <p className="text-gray-600 mb-6">It looks like there's no test data to display.</p>
          <button
            className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors w-full"
            onClick={() => navigate('/dashboard')}
          >
            <FaHome /> Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // PDF Generation Function
  const generatePDF = () => {
    setIsGeneratingPDF(true);

    try {
      const doc = new jsPDF("p", "pt", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let y = 40;

      /* =========================
         TOP BRAND HEADER
      ========================= */
      doc.setFont("helvetica", "bold");
      doc.setFontSize(20);
      doc.setTextColor(33, 37, 41);
      doc.text("Shorthand Software Academy", pageWidth / 2, y, { align: "center" });

      y += 18;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(
        "Gmail: shorthandsoftwares@gmail.com | WhatsApp: +91 83170 27646",
        pageWidth / 2,
        y,
        { align: "center" }
      );

      y += 12;
      doc.setDrawColor(220);
      doc.line(40, y, pageWidth - 40, y);
      y += 30;

      /* =========================
         MAIN TITLE BAND
      ========================= */
      doc.setFillColor(59, 130, 246);
      doc.rect(0, y, pageWidth, 70, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(24);
      doc.setTextColor(255);
      doc.text("Test Result Report", pageWidth / 2, y + 40, { align: "center" });

      if (state?.testTitle) {
        doc.setFontSize(13);
        doc.setFont("helvetica", "normal");
        doc.text(`Dictation Name: ${state.testTitle}`, pageWidth / 2, y + 60, {
          align: "center",
        });
      }

      y += 95;

      /* =========================
         SECTION TITLE HELPER
      ========================= */
      const sectionTitle = (title) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(15);
        doc.setTextColor(40);
        doc.text(title, 40, y);

        y += 8;
        doc.setDrawColor(200);
        doc.line(40, y, pageWidth - 40, y);
        y += 22;
      };

      /* =========================
         PERFORMANCE OVERVIEW
      ========================= */
      sectionTitle("Performance Overview");

      const accuracy = stats.accuracy;
      const level =
        accuracy >= 90 ? "Excellent" :
          accuracy >= 80 ? "Good" :
            accuracy >= 70 ? "Fair" : "Needs Practice";

      const overview = [
        ["Overall Accuracy", `${accuracy}% (${level})`],
        ["Total Words", stats.totalWords],
        ["Correct Words", stats.correct],
        ["Mistakes", stats.totalMistake],
        ["Typing Speed", `${stats.dictationSpeed} WPM`],
      ];

      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.setTextColor(60);

      overview.forEach(([label, val]) => {
        doc.text(label, 50, y);
        doc.setFont("helvetica", "bold");
        doc.text(String(val), pageWidth - 50, y, { align: "right" });
        doc.setFont("helvetica", "normal");
        y += 20;
      });

      y += 20;

      /* =========================
         DETAILED STATISTICS
      ========================= */
      sectionTitle("Detailed Statistics");

      autoTable(doc, {
        startY: y,
        head: [["Metric", "Count", "Percentage"]],
        body: [
          ["Total Words", stats.totalWords, "100%"],
          ["Correct Words", stats.correct, `${((stats.correct / stats.totalWords) * 100).toFixed(1)}%`],
          ["Wrong Words", stats.mistake, `${((stats.mistake / stats.totalWords) * 100).toFixed(1)}%`],
          ["Missing Words", stats.missing, `${((stats.missing / stats.totalWords) * 100).toFixed(1)}%`],
          ["Punctuation Errors", stats.punctuation || 0, `${(((stats.punctuation || 0) / stats.totalWords) * 100).toFixed(1)}%`],
        ],
        theme: "grid",
        headStyles: {
          fillColor: [59, 130, 246],
          textColor: 255,
          fontStyle: "bold",
          halign: "center",
        },
        bodyStyles: {
          halign: "center",
          fontSize: 11,
        },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        margin: { left: 40, right: 40 },
      });

      y = doc.lastAutoTable.finalY + 30;

      /* =========================
         MISTAKE ANALYSIS
      ========================= */
      if (stats?.userWords && stats?.refWords) {
        sectionTitle("Mistake Analysis");

        const mistakeDetails = getMistakeDetails();
        if (mistakeDetails.length) {
          autoTable(doc, {
            startY: y,
            head: [["#", "Typed Word", "Correct Word", "Error Type"]],
            body: mistakeDetails.map(m => [
              m.position,
              m.userWord,
              m.refWord,
              m.type === 'punctuation' ? "Punctuation" :
                m.type === 'capitalization' ? "Capitalization" :
                  m.type === 'substitution' ? "Substitution" :
                    "Incorrect"

            ]),
            theme: "grid",
            headStyles: {
              fillColor: [220, 53, 69],
              textColor: 255,
              fontStyle: "bold",
            },
            bodyStyles: { fontSize: 10 },
            margin: { left: 40, right: 40 },
          });

          y = doc.lastAutoTable.finalY + 30;
        }
      }

      /* =========================
         USER TYPED TEXT
      ========================= */
      sectionTitle("Your Typed Text");

      const typedWrapped = doc.splitTextToSize(
        stats?.userWords.join(" "),
        pageWidth - 80
      );

      doc.setFontSize(11);
      typedWrapped.forEach(line => {
        if (y > pageHeight - 60) {
          doc.addPage();
          y = 40;
        }
        doc.text(line, 40, y);
        y += 16;
      });

      y += 30;

      /* =========================
         REFERENCE TEXT
      ========================= */
      sectionTitle("Correct Reference Text");

      const correctWrapped = doc.splitTextToSize(
        stats?.refWords.join(" "),
        pageWidth - 80
      );

      correctWrapped.forEach(line => {
        if (y > pageHeight - 60) {
          doc.addPage();
          y = 40;
        }
        doc.text(line, 40, y);
        y += 16;
      });

      /* =========================
         FOOTER
      ========================= */
      doc.setFontSize(9);
      doc.setTextColor(150);
      doc.text(
        "Generated by Typing Test Application",
        pageWidth / 2,
        pageHeight - 25,
        { align: "center" }
      );

      doc.save(`typing-results-${Date.now()}.pdf`);

    } catch (err) {
      console.error("PDF ERROR:", err);
      alert("Failed to generate PDF.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const downloadResultAsPDF = async () => {
    try {
      setIsGeneratingPDF(true);

      const element = contentRef.current;

      const canvas = await html2canvas(element, {
        scale: 2,              // High quality
        useCORS: true,
        backgroundColor: '#1d3a80',
        scrollY: -window.scrollY
      });

      const imgData = canvas.toDataURL('image/png');

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      pdf.save(`Result-${Date.now()}.pdf`);
    } catch (err) {
      console.error('PDF Download Error:', err);
      alert('Failed to download result');
    } finally {
      setIsGeneratingPDF(false);
    }
  };



  const getFullAndHalfMistakes = () => {
  const rule = stats?.selectedRule;
  const penalties = stats?.detailedPenalties || {};

  if (!rule) {
    return { full: 0, half: 0 };
  }

  let full = 0;
  let half = 0;

  // ✅ CUSTOM RULE SUPPORT
  if (rule.isCustom && rule.rules) {
    rule.rules.forEach(r => {
      const count = penalties[r.mistakeId]?.count || 0;

      if (r.mistakeType === 'full') {
        full += count;
      } else if (r.mistakeType === 'half') {
        half += count;
      }
    });

    return { full, half };
  }

  // ✅ PREDEFINED RULE SUPPORT (existing logic)
  const fullTypes = rule.fullMistakes?.map(m => m.type) || [];
  const halfTypes = rule.halfMistakes?.map(m => m.type) || [];

  Object.entries(penalties).forEach(([type, data]) => {
    if (fullTypes.includes(type)) {
      full += data.count;
    }
    if (halfTypes.includes(type)) {
      half += data.count;
    }
  });

  return { full, half };
};



  const { full: totalFullMistakes, half: totalHalfMistakes } =
    getFullAndHalfMistakes();


  const generateComparisonTextForPDF = () => {
    const { userWords = [], refWords = [] } = stats || {};
    const maxLen = Math.max(userWords.length, refWords.length);
    let comparison = '';

    for (let i = 0; i < maxLen; i++) {
      const userWord = i < userWords.length ? userWords[i] : null;
      const refWord = i < refWords.length ? refWords[i] : null;

      if (userWord && refWord && userWord === refWord) {
        comparison += refWord + ' ';
      }
      else if (userWord && refWord && userWord !== refWord) {
        comparison += `[${userWord}→${refWord}] `;
      }
      else if (!userWord && refWord) {
        comparison += `[MISSING:${refWord}] `;
      }
      else if (userWord && !refWord) {
        comparison += `[EXTRA:${userWord}] `;
      }
    }

    return comparison;
  };

  const getMistakeDetails = () => {
    return (stats.comparison || [])
      .map((c, idx) => ({ ...c, position: idx + 1 }))
      .filter(c => c.type !== 'correct');
  };


  // Handle stat card click
  const handleStatClick = (statType) => {
    setActiveStat(statType);
    setShowMistakeDetails(true);
    document.body.style.overflow = 'hidden';
  };

  // Close modal and re-enable body scroll
  const closeModal = () => {
    setShowMistakeDetails(false);
    document.body.style.overflow = 'unset';
  };


  function formatDateSimple(dateStr) {
    const parts = dateStr.split('/');
    // parts[0] = month, parts[1] = day, parts[2] = year
    return `${parts[1]}-${parts[0]}-${parts[2]}`;
  }

  const renderMistakeDetails = () => {
    if (!activeStat) return null;

    const getTitle = () => {
      switch (activeStat) {
        case 'total':
          return 'Total Words Analysis';
        case 'correct':
          return 'Correct Words';
        case 'mistake':
          return 'Mistake Analysis';
        case 'missing':
          return 'Missing Words';
        case 'punctuation':
          return 'Punctuation Mistakes';
        case 'capitalization':
          return 'Capitalization Mistakes';
        case 'speed':
          return 'Typing Speed Analysis';
        default:
          return 'Performance Details';
      }
    };

    const getContent = () => {
      switch (activeStat) {
        case 'total':
          return {
            count: stats.totalWords,
            description: 'Total number of words in the exercise',
            icon: <FaBook className="text-blue-500 text-4xl" />,
            color: 'bg-blue-100 text-blue-600',
            showDetails: false
          };
        case 'correct':
          return {
            count: stats.correct,
            description: 'Words you typed correctly',
            icon: <FaCheck className="text-green-500 text-4xl" />,
            color: 'bg-green-100 text-green-600',
            showDetails: false
          };
        case 'mistake':
          return {
            count: stats.totalMistake,
            description: 'Words that need attention',
            icon: <FaTimes className="text-red-500 text-4xl" />,
            color: 'bg-red-100 text-red-600',
            showDetails: true,
            details: getMistakeDetails()
          };
        case 'speed':
          return {
            count: stats.dictationSpeed,
            description: 'Words per minute (WPM)',
            icon: <GiSpeedometer className="text-purple-500 text-4xl" />,
            color: 'bg-purple-100 text-purple-600',
            showDetails: false
          };
        default:
          return {
            count: 0,
            description: 'No data available',
            icon: <FaInfoCircle className="text-gray-500 text-4xl" />,
            color: 'bg-gray-100 text-gray-600',
            showDetails: false
          };
      }
    };

    const content = getContent();

    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-gray-800">{getTitle()}</h3>
          <button
            onClick={closeModal}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FaTimes className="text-xl" />
          </button>
        </div>

        <div className="text-center py-8">
          <div className={`w-20 h-20 ${content.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
            {content.icon}
          </div>
          <div className="text-4xl font-bold mb-2">{content.count}</div>
          <p className="text-gray-600 mb-6">{content.description}</p>

          {activeStat === 'speed' && (
            <div className="mt-4">
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full ${stats.dictationSpeed > 50 ? 'bg-green-500' :
                    stats.dictationSpeed > 30 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                  style={{ width: `${Math.min(stats.dictationSpeed, 100)}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                {stats.dictationSpeed > 50 ? 'Fast' : stats.dictationSpeed > 30 ? 'Average' : 'Slow'} typing speed
              </p>
            </div>
          )}

          {content.showDetails && content.details && content.details.length > 0 && (
            <div className="mt-6 space-y-4">
              <h4 className="font-medium text-gray-800">Mistake Details:</h4>
              <div className="bg-gray-50 p-4 rounded-lg max-h-64 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">You Typed</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Correct Word</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {content.details.map((mistake, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-left whitespace-nowrap text-sm text-gray-500">{mistake.position}</td>
                        <td className="px-4 py-2 text-left whitespace-nowrap">
                          <span className="text-red-500">
                            {mistake.userWord}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-left whitespace-nowrap">
                          <span className="text-green-600">
                            {mistake.refWord}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-left whitespace-nowrap text-sm">
                          {mistake.isPunctuation ? (
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                              Punctuation
                            </span>
                          ) : mistake.isCapitalization ? (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                              Capitalization
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                              Incorrect
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 text-sm text-gray-500">
                <p>Total mistakes: {content.details.length}</p>
                <div className="flex gap-4 mt-2">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                    Incorrect: {content.details.filter(m => !m.isPunctuation && !m.isCapitalization).length}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-purple-500 rounded-full"></span>
                    Punctuation: {content.details.filter(m => m.isPunctuation).length}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                    Capitalization: {content.details.filter(m => m.isCapitalization).length}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={closeModal}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  };

  const renderWord = (item) => {
    switch (item.type) {
      case 'correct':
        return (
          <span className="text-black px-1">
            {item.refWord}{' '}
          </span>
        );


      case 'missing':
        return (
          <span className="text-gray-700 bg-gray-100 px-1 rounded">
            {item.refWord}{' '}
          </span>
        );

      case 'extra':
        return (
          <span className="text-red-500 bg-red-100 line-through px-1 rounded">
            {item.userWord}{' '}
          </span>
        );

      case 'substitution':
        return (
          <>
            <span className="text-red-600 bg-red-100 line-through px-1 rounded">
              {item.userWord}
            </span>
            <span className="text-green-700 bg-green-100 px-1 rounded ml-1">
              {item.refWord}{' '}
            </span>
          </>
        );


      case 'capitalization':
        return (
          <>
            <span className="text-red-500 bg-red-100 line-through px-1 rounded">
              {item.userWord}
            </span>
            <span className="text-blue-600 bg-blue-100 px-1 rounded ml-1">
              {item.refWord}{' '}
            </span>
          </>
        );


      case 'punctuation':
        return (
          <>
            <span className="text-red-500 bg-red-100 line-through px-1 rounded">
              {item.userWord}{' '}
            </span>
            <span className="text-purple-600 bg-purple-100 px-1 rounded">
              {item.refWord}{' '}
            </span>
          </>
        );

      default: // spelling
        return (
          <>
            <span className="text-red-500 bg-red-100 line-through px-1 rounded">
              {item.userWord}
            </span>
            <span className="text-yellow-600 bg-yellow-100 px-1 rounded ml-1">
              {item.refWord}{' '}
            </span>
          </>
        );
    }
  };


  const renderComparison = () => {
    const comparison = stats.comparison || [];

    return (
      <div className="whitespace-pre-wrap break-words leading-8 text-lg">
        {comparison.map((item, idx) => {
          const isActive = activeErrorType === item.type;

          return (
            <span
              key={idx}
              ref={el => {
                if (!el) return;
                if (!errorRefs.current[item.type]) {
                  errorRefs.current[item.type] = [];
                }
                errorRefs.current[item.type].push(el);
              }}

              className={`inline-block mx-0.5 px-1 rounded transition-all
              ${isActive ? 'ring-2 ring-offset-2 ring-black animate-pulse' : ''}
            `}
            >
              {renderWord(item)}
            </span>
          );
        })}
      </div>
    );
  };



  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // Calculate performance metrics
  const accuracyPercentage = stats.accuracy;
  const performanceLevel = accuracyPercentage >= 90 ? 'Excellent' :
    accuracyPercentage >= 80 ? 'Good' :
      accuracyPercentage >= 70 ? 'Fair' : 'Needs Practice';

  return (
    <div className="min-h-screen bg-[#1d3a80]  font-sans relative">
      {/* Mistake Details Modal with Escape key and click outside to close */}
      <AnimatePresence>
        {showMistakeDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={closeModal}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {renderMistakeDetails()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* PDF Loading Overlay */}
      <AnimatePresence>
        {isGeneratingPDF && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm"
          >
            <LogoLoader text="Generating PDF, please wait..." />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Report Modal */}
      <AnimatePresence>
        {showReportModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 
                 bg-black/40 backdrop-blur-md"
            onClick={() => !isSubmittingReport && setShowReportModal(false)}
          >
            <motion.div
              className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl 
                   w-full max-w-lg overflow-hidden border border-white/40"
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-5">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-white tracking-wide">
                    Report Transcription
                  </h3>

                  <button
                    onClick={() => setShowReportModal(false)}
                    disabled={isSubmittingReport}
                    className="text-white/80 hover:text-white transition"
                  >
                    <FaTimes className="text-2xl" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {!reportSubmitted ? (
                  <form onSubmit={handleReportSubmit} className="space-y-5">

                    {/* Reason Select */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Reason for Reporting
                      </label>
                      <div className="relative">
                        <select
                          className="w-full p-3 rounded-xl border border-gray-300 
                               shadow-sm focus:ring-2 focus:ring-blue-500 
                               focus:border-blue-500 transition bg-white"
                          value={reportReason}
                          onChange={(e) => setReportReason(e.target.value)}
                          required
                          disabled={isSubmittingReport}
                        >
                          <option value="" disabled>Select a reason</option>
                          {reportReasons.map((reason) => (
                            <option key={reason} value={reason}>
                              {reason}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Other Reason Box */}
                    {reportReason === "Other (please specify)" && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                          Please specify
                        </label>

                        <textarea
                          className="w-full p-3 rounded-xl border border-gray-300 
                               shadow-sm focus:ring-2 focus:ring-blue-500 
                               focus:border-blue-500 transition"
                          rows="3"
                          placeholder="Describe the issue in detail..."
                          value={reportReason}
                          onChange={(e) => setReportReason(e.target.value)}
                          required
                          disabled={isSubmittingReport}
                        />
                      </div>
                    )}

                    {/* Buttons */}
                    <div className="flex justify-end gap-3 pt-3">
                      <button
                        type="button"
                        onClick={() => setShowReportModal(false)}
                        disabled={isSubmittingReport}
                        className="px-5 py-2.5 rounded-xl border border-gray-300 
                             text-gray-700 hover:bg-gray-100 transition font-medium"
                      >
                        Cancel
                      </button>

                      <button
                        type="submit"
                        disabled={isSubmittingReport || !reportReason.trim()}
                        className="px-6 py-2.5 rounded-xl bg-yellow-500 text-white
                             hover:bg-yellow-600 transition font-semibold
                             shadow-md flex items-center gap-2"
                      >
                        {isSubmittingReport ? (
                          <>
                            <svg
                              className="animate-spin h-5 w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 
                             0 0 5.373 0 12h4zm2 
                             5.291A7.962 7.962 0 014 12H0c0 
                             3.042 1.135 5.824 3 
                             7.938l3-2.647z"
                              />
                            </svg>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <FaExclamationTriangle />
                            Submit Report
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                ) : (
                  // Submitted State
                  <div className="text-center py-10">
                    <div className="w-20 h-20 mx-auto mb-4 bg-green-100 rounded-full 
                              flex items-center justify-center shadow-inner">
                      <FaCheck className="text-green-600 text-3xl" />
                    </div>

                    <h4 className="text-xl font-bold text-gray-800 mb-2">
                      Report Submitted
                    </h4>
                    <p className="text-gray-600 leading-relaxed">
                      Thank you for your feedback. Our team will review the transcription soon.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      <div className="max-w-6xl mx-auto font-[montserrat] bg-[#1d3a80]" ref={contentRef}>
        <div className="mb-2">

          {/* ================= HEADER ================= */}
          <div className="bg-[#1d3a80] rounded-2xl px-2 py-2">
            <div
              className="
        max-w-md mx-auto
        flex items-center justify-center gap-5
        text-white
      "
            >
              {/* LOGO (LEFT) */}
              <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-lg">
                <img
                  src={Logo}
                  alt="Logo"
                  className="w-14 h-14 object-contain"
                />
              </div>

              {/* DETAILS (RIGHT) */}
              <div className="text-left leading-tight">
                <h1 className="text-lg font-bold">
                  Shorthand Software Academy
                </h1>

                <p className="text-sm text-white/90">
                  shorthandsoftwares@gmail.com
                </p>

                <p className="text-sm text-white/90">
                  WhatsApp: +91 8317027646
                </p>
              </div>
            </div>
          </div>


        </div>





        {/* Performance Overview */}
        <div className="bg-[#f3f5f9] rounded-xl p-3 shadow-lg">

          {/* TITLE */}
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            Result Sheet
          </h2>

          {/* ROW 1 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-2">
            <div className="bg-[#1f3c88] text-white rounded-lg px-4 py-3 font-semibold">
              Dictation Name: {state?.topicName}
            </div>
            <div className="bg-[#1f3c88] text-white rounded-lg px-4 py-3 font-semibold">
              Test Date: {formatDateSimple(stats?.date)}
            </div>
            <div className="bg-[#2f8f9d] text-white rounded-lg px-4 py-3 font-semibold">
              Total Words: {stats.totalWords}
            </div>
            <div className="bg-[#4aa3df] text-white rounded-lg px-4 py-3 font-semibold">
              Typed Words: {stats.typedWords}
            </div>
          </div>

          {/* ROW 2 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-2">
            <div className="bg-[#c44536] text-white rounded-lg px-4 py-3 font-semibold">
              Full Mistakes: {totalFullMistakes}
            </div>
            <div className="bg-[#e58e26] text-white rounded-lg px-4 py-3 font-semibold">
              Half Mistakes: {totalHalfMistakes}
            </div>
            <div className="bg-[#6c5ce7] text-white rounded-lg px-4 py-3 font-semibold">
              Punctuation: {stats.punctuationMistake || 0}
            </div>
            <div className="bg-[#273c75] text-white rounded-lg px-4 py-3 font-semibold">
              No. of Total Mistakes: {totalFullMistakes * 1 + totalHalfMistakes * 0.5}
            </div>
          </div>

          {/* ROW 3 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-2">
            <div className="bg-[#16a085] text-white rounded-lg px-4 py-3 font-semibold">
              Accuracy: {accuracyPercentage}%
            </div>
            <div className="bg-[#3498db] text-white rounded-lg px-4 py-3 font-semibold">
              Keystrokes: {stats.keystrokes}
            </div>
            <div className="bg-[#40739e] text-white rounded-lg px-4 py-3 font-semibold">
              Speed: {stats.dictationSpeed} WPM
            </div>
            <button
              onClick={() => setShowReportModal(true)}
              className="bg-[#b33939] hover:bg-[#9c2f2f] text-white rounded-lg py-3 font-semibold transition"
            >
              🚩 Report
            </button>
          </div>

          {/* ACTION BUTTONS */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

            <button
              className="bg-[#273c75] hover:bg-[#1e2f5c] text-white rounded-lg py-3 font-semibold transition"
            >
              🔄 Re-evaluate
            </button>

            <button
              onClick={downloadResultAsPDF}
              className="bg-[#218c74] hover:bg-[#1e7a65] text-white rounded-lg py-3 font-semibold transition"
            >
              📄 Download Report
            </button>

            <button
              onClick={() => setShowCompareModal(true)}
              className="bg-[#6c5ce7] hover:bg-[#5a4bcf] text-white rounded-lg py-3 font-semibold transition"
            >
              📑 Compare Passage
            </button>

          </div>

          {errorBreakdown.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              {errorBreakdown.map(([type, data]) => {
                const badge = ERROR_BADGES[type];
                if (!badge) return null;

                return (
                  <div
                    key={type}
                    onClick={() => {
                      const targetType = badge.mapTo || type;
                      setActiveErrorType(prev =>
                        prev === targetType ? null : targetType
                      );
                    }}


                    className={`flex items-center gap-2 px-4 py-2 rounded-full cursor-pointer hover:scale-105 transition ${badge.bg}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${badge.dot}`}></span>

                    <span className={`text-sm font-semibold ${badge.text}`}>
                      {badge.label}
                    </span>

                    <span className="text-xs font-bold text-gray-700">
                      ({data.count})
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>



        {/* Text Comparison */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-2 border border-blue-100 mt-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <FaBook className="text-blue-500" />
              Your Writing Analysis
            </h2>
            <div className="text-sm text-gray-500">
              Accuracy: <span className="font-bold text-blue-600">{accuracyPercentage}%</span>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-lg leading-relaxed min-h-[200px]">
            {renderComparison()}
          </div>

          {/* Transcription Sections */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Original Transcription */}
            {/* <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <FaBook className="text-blue-500" />
                Original Transcription
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 h-48 overflow-y-auto">
                {stats?.refWords?.join(' ') || 'No original transcription available.'}
              </div>
            </div> */}

            {/* Your Typed Text */}
            {/* <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <GiPencil className="text-green-500" />
                Your Typed Text
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 h-48 overflow-y-auto">
                {stats?.userWords?.join(' ') || 'No typed text available.'}
              </div>
            </div> */}
          </div>

        </div>

        {/* Study Tips */}
        {accuracyPercentage < 90 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-yellow-800 mb-3 flex items-center gap-2">
              <FaStar className="text-yellow-600" />
              Tips for Improvement
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="text-yellow-700 text-sm">
                • Practice typing the same text multiple times
              </div>
              <div className="text-yellow-700 text-sm">
                • Focus on accuracy before speed
              </div>
              <div className="text-yellow-700 text-sm">
                • Review punctuation rules
              </div>
              <div className="text-yellow-700 text-sm">
                • Take your time and double-check
              </div>
            </div>
          </div>
        )}

        {/* Celebration for high scores */}
        {accuracyPercentage >= 90 && (
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-6 text-center text-white">
            <FaStar className="text-4xl mx-auto mb-3" />
            <h3 className="text-xl font-bold mb-2">Outstanding Work! 🎉</h3>
            <p className="opacity-90">You're doing amazing! Keep up the great practice.</p>
          </div>
        )}
      </div>
      <ComparePassageModal
        isOpen={showCompareModal}
        onClose={() => setShowCompareModal(false)}
        stats={stats}
      />

    </div>
  );
};

export default ExamResult;